import { AppstoreOutlined, UnorderedListOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import { Card, Empty, Flex, Segmented, Skeleton } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { RoutesPaths } from "../../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useRenameAttachment } from "../../../../../../hooks/use-rename-attachment.ts"
import type { RootState } from "../../../../../../store.ts"
import { validateFile } from "../../../../../../utils/file-validation.ts"
import {
  type AlbumView,
  useAlbumView
} from "../../../../../Album/helpers/album-view-preference.ts"
import { albumSetProp } from "../../../../../Album/redux/album.slice.ts"
import {
  addAlbumAttachments,
  deleteAlbum,
  fetchAlbumFilterOptions,
  getAlbum,
  removeAlbumAttachments,
  updateAlbum
} from "../../../../../Album/redux/album.thunks.ts"
import type { AlbumsFilters } from "../../../../../Album/redux/album.types.ts"
import { renameAttachment } from "../../../../../Album/redux/attachments.thunks.ts"
import type { AlbumDeleteFilesMode } from "../../../../../Album/types/album.types.ts"
import type { Album } from "../../../../../Album/types/album.types.ts"
import { AttachmentModels, type Attachment } from "../../../../../Album/types/attachment.types.ts"
import { FileViewer } from "../../../../../Files/components/FileViewer.tsx"
import { useFavorites } from "../../../../../Files/hooks/useFavorites.ts"
import { mapAttachmentToFileItem } from "../../../../../Files/helpers/map-to-file-item.ts"
import { type FileItem, type FileRights } from "../../../../../Files/types/file-item.types.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { uploadProjectAttachment } from "../../../../redux/project-attachments/project-attachments.thunks.ts"
import { AlbumFilesSettings } from "../../../constants/album-files-settings.tsx"
import { mapAttachmentToUploadFile } from "../../../helpers/map-attachments-to-file-list.ts"
import { FileDragOverlay } from "../../FileDragOverlay.tsx"
import { RenameFileModal } from "../../RenameFileModal.tsx"
import { AttachmentSelector } from "../ProjectViewAlbums/AttachmentSelector/AttachmentSelector.tsx"
import { DeleteAlbumModal } from "../ProjectViewAlbums/DeleteAlbum/DeleteAlbumModal.tsx"
import { EditAlbumModal } from "../ProjectViewAlbums/EditAlbum/EditAlbumModal.tsx"
import { ProjectViewAlbumAttachments } from "./ProjectViewAlbumAttachments.tsx"
import { ProjectViewAlbumFilesTable } from "./ProjectViewAlbumFilesTable.tsx"
import { ProjectViewAlbumSectionFilters } from "./ProjectViewAlbumSectionFilters.tsx"
import { ProjectViewAlbumSectionInfo } from "./ProjectViewAlbumSectionInfo.tsx"

const ALBUM_UPLOAD_ACCEPT = AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",")

function ProjectViewAlbumSection({
  userId,
  album,
  fetching,
  filters,
  projectPermissions,
  getAlbumById,
  getFilterOptions,
  updateAlbum,
  setProp,
  addAlbumAttachments,
  removeAlbumAttachments,
  deleteAlbum,
  renameAttachment
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const params = useParams() as Record<string, string>

  const albumId = params?.albumId
  const projectId = params?.id
  const moduleDocs = projectPermissions?.docs
  const addFilesAllowed = moduleDocs?.project_doc_create || false
  const attachments = album?.attachments || []

  const deleteAllowed = !!moduleDocs?.project_doc_delete
  const deleteForeignAllowed =
    deleteAllowed && !!moduleDocs?.project_doc_manage_others

  const editAllowed = !!moduleDocs?.project_doc_edit
  const editForeignAllowed =
    editAllowed && !!moduleDocs?.project_doc_manage_others

  const deleteAlbumAllowed =
    deleteForeignAllowed || (deleteAllowed && userId === album?.created_by)

  const { t } = useTranslation()
  const [albumView, setAlbumView] = useAlbumView()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const [isAttachmentSelectorOpen, setIsAttachmentSelectorOpen] =
    useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([])
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)
  const [renamingFileItem, setRenamingFileItem] = useState<FileItem | null>(
    null
  )
  // const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
  const fileInputRef = useRef<HTMLInputElement>(null)
  const favorites = useFavorites(projectId)

  const rights: FileRights = useMemo(
    () => ({
      currentUserId: userId,
      editAllowed,
      editForeignAllowed,
      deleteAllowed,
      deleteForeignAllowed
    }),
    [userId, editAllowed, editForeignAllowed, deleteAllowed, deleteForeignAllowed]
  )

  const previewItems = useMemo(
    () => attachments.map((a) => mapAttachmentToFileItem(a, rights)),
    [attachments, rights]
  )

  // Merge uploading files with fetched attachments
  const fileList = useMemo(() => {
    const fetchedFiles = attachments.map(mapAttachmentToUploadFile)
    const fetchedIds = new Set(attachments.map((a) => a.id))

    // Keep only uploading files that aren't in fetched yet
    const pendingUploads = uploadingFiles.filter((f) => !fetchedIds.has(f.uid))

    return [...pendingUploads, ...fetchedFiles]
  }, [attachments, uploadingFiles])

  const setFilters = useCallback(
    (data: Partial<AlbumsFilters>) => {
      setProp({
        key: "albumFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleFilterChange = useCallback(
    async (data: Partial<AlbumsFilters>) => {
      setFilters({ ...data, page: 1 })
    },
    [setFilters]
  )

  const fetchAlbum = useCallback(() => {
    if (!albumId) return
    return getAlbumById({ id: albumId, filters })
  }, [albumId, getAlbumById, filters])

  const handleGetFilterOptions = useCallback(() => {
    return getFilterOptions(albumId)?.unwrap()
  }, [getFilterOptions, albumId])

  const handleBack = () => {
    navigate(`${RoutesPaths.project.view(projectId).files}?nav=albums`)
  }

  const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen)
  const toggleAttachmentsSelector = () =>
    setIsAttachmentSelectorOpen(!isAttachmentSelectorOpen)

  const handleEditSubmit = async (data: Partial<Album>) => {
    if (!albumId) return

    await updateAlbum({ id: albumId, data: { ...data } })
    toggleEditModal()
  }

  const handleDelete = useCallback(
    async (files: AlbumDeleteFilesMode) => {
      if (!albumId) return
      setIsDeletingAlbum(true)
      try {
        // `unwrap` is the point: a rejected delete otherwise settles as a
        // fulfilled promise and we navigate away from an album still there.
        await deleteAlbum({ id: albumId, files }).unwrap()
      } catch {
        return
      } finally {
        setIsDeletingAlbum(false)
      }
      setIsDeleteModalOpen(false)
      navigate(`${RoutesPaths.project.view(projectId).files}?nav=albums`)
    },
    [albumId, deleteAlbum, navigate, projectId]
  )

  const onDeleteClick = useCallback(() => setIsDeleteModalOpen(true), [])

  const handleAddAttachments = async (attachmentIds: string[]) => {
    try {
      if (!albumId) return

      await addAlbumAttachments({
        id: albumId,
        data: { attachment_ids: attachmentIds }
      })

      setIsAttachmentSelectorOpen(false)
      await handleGetFilterOptions()
      await fetchAlbum()
    } catch (e) {
      console.log("Error adding attachments:", e)
    }
  }

  const handleRemoveAttachment = useCallback(
    async (attachmentId: string) => {
      if (!albumId) return

      setDeletingIds((prev) => new Set(prev).add(attachmentId))

      try {
        await removeAlbumAttachments({
          id: albumId,
          data: { attachment_ids: [attachmentId] }
        })

        // Remove from uploadingFiles if it was uploaded in this session
        setUploadingFiles((prev) => prev.filter((f) => f.uid !== attachmentId))

        await handleGetFilterOptions()
      } catch (e) {
        console.log("Error removing attachment:", e)
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev)
          next.delete(attachmentId)
          return next
        })
      }
    },
    [albumId, removeAlbumAttachments, handleGetFilterOptions]
  )

  const handleRename = useRenameAttachment(renameAttachment)

  const handlePreviewFile = useCallback(
    (file: UploadFile) => {
      const attachment = file.response as Attachment | undefined
      if (!attachment) return
      setPreviewFile(mapAttachmentToFileItem(attachment, rights))
    },
    [rights]
  )

  const viewerFileActions = useCallback(
    (viewerFile: FileItem): MenuItemType[] => {
      const items: MenuItemType[] = []
      if (viewerFile.canRename) {
        items.push({
          key: "rename",
          label: t("files.actions.rename"),
          onClick: () => setRenamingFileItem(viewerFile)
        })
      }
      if (viewerFile.canDelete) {
        items.push({
          key: "delete",
          danger: true,
          label: t("files.actions.delete"),
          onClick: () => void handleRemoveAttachment(viewerFile.id)
        })
      }
      return items
    },
    [t, handleRemoveAttachment]
  )

  const handleUpload = useCallback(
    async (file: File) => {
      if (!albumId || !projectId) return

      const uid = crypto.randomUUID()
      const abortController = new AbortController()

      // abortControllersRef.current.set(uid, abortController)

      setUploadingFiles((prev) => [
        {
          uid,
          name: file.name,
          status: "uploading",
          percent: 0
        },
        ...prev
      ])

      try {
        const res = await dispatch(
          uploadProjectAttachment({
            data: {
              model_type: AttachmentModels.PROJECT,
              object_id: projectId,
              project_id: projectId,
              file
            },
            signal: abortController.signal
          })
        ).unwrap()

        // abortControllersRef.current.delete(uid)

        // Add attachment to album
        await addAlbumAttachments({
          id: albumId,
          data: { attachment_ids: [res.id] }
        })

        // // Update uploading file with response data
        // setUploadingFiles((prev) =>
        //   prev.map((f) =>
        //     f.uid === uid
        //       ? {
        //           ...f,
        //           uid: res.id,
        //           status: "done",
        //           url: res.file,
        //           size: res.file_size,
        //           type: res.mime_type,
        //           response: res
        //         }
        //       : f
        //   )
        // )

        // Update uploading file with response data
        setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid))

        await fetchAlbum()

        // await handleGetFilterOptions()
      } catch (e) {
        // abortControllersRef.current.delete(uid)

        if (abortController.signal.aborted) {
          setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid))
          return
        }

        setUploadingFiles((prev) =>
          prev.map((f) => (f.uid === uid ? { ...f, status: "error" } : f))
        )
      }
    },
    [dispatch, albumId, projectId, fetchAlbum, addAlbumAttachments]
  )
  //
  // const handleCancelUpload = useCallback((uid: string) => {
  //   const abortController = abortControllersRef.current.get(uid)
  //   if (abortController) {
  //     abortController.abort()
  //     abortControllersRef.current.delete(uid)
  //   }
  // }, [])

  const handleBeforeUpload = useCallback(
    (file: File) => {
      const result = validateFile(file, {
        maxSize: AlbumFilesSettings.MAX_SIZE_MB,
        allowedExtensions: AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS,
        defaultResult: true
      })

      if (result === true) {
        void handleUpload(file)
      }

      return false
    },
    [handleUpload]
  )

  const handleAddFileClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFilesPicked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      Array.from(event.target.files ?? []).forEach(handleBeforeUpload)
      event.target.value = ""
    },
    [handleBeforeUpload]
  )

  useEffect(() => {
    void fetchAlbum()
  }, [fetchAlbum, albumId])

  useEffect(() => {
    getFilterOptions(albumId)
  }, [getFilterOptions, albumId])

  if (!album) {
    if (fetching) {
      return <Skeleton.Node active className={"size-full"} />
    }

    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Empty description="Album not found" />
      </div>
    )
  }

  return (
    <Flex vertical gap="small" flex={1} className="overflow-hidden w-full">
      <ProjectViewAlbumSectionInfo
        album={album}
        editAllowed={moduleDocs?.project_doc_edit || false}
        addFilesAllowed={addFilesAllowed}
        onEditClick={toggleEditModal}
        onBackClick={handleBack}
        onSelectFileClick={toggleAttachmentsSelector}
        onAddFileClick={handleAddFileClick}
        deleteAllowed={deleteAlbumAllowed}
        onDeleteClick={onDeleteClick}
      />

      {addFilesAllowed && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALBUM_UPLOAD_ACCEPT}
          className="hidden"
          data-testid="album-file-input"
          onChange={handleFilesPicked}
        />
      )}

      <ProjectViewAlbumSectionFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        getFilterOptions={handleGetFilterOptions}
      />

      <Card
        title="Files"
        className="mt-4 overflow-hidden flex-1 flex flex-col"
        classNames={{
          body: "overflow-y-auto tailwind-scroll overflow-x-hidden flex-1 p-3"
        }}
      >
        <FileDragOverlay
          message="Drag and drop files here"
          acceptedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
          maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
          onFiles={(files) => files.forEach(handleBeforeUpload)}
        />

        <Flex justify={"end"}>
          <Segmented<AlbumView>
            value={albumView}
            onChange={setAlbumView}
            options={[
              {
                value: "grid",
                icon: <AppstoreOutlined />,
                title: t("albumView.grid")
              },
              {
                value: "table",
                icon: <UnorderedListOutlined />,
                title: t("albumView.table")
              }
            ]}
          />
        </Flex>

        {albumView === "table" && !fetching ? (
          <ProjectViewAlbumFilesTable
            fileList={fileList}
            currentUserId={userId}
            deleteAllowed={deleteAllowed}
            deleteForeignAllowed={deleteForeignAllowed}
            editAllowed={editAllowed}
            editForeignAllowed={editForeignAllowed}
            deletingIds={deletingIds}
            onDelete={handleRemoveAttachment}
            onRename={handleRename}
            onPreviewFile={handlePreviewFile}
          />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 min-h-64">
            {fetching ? (
              Array.from({ length: 20 }).map((_, i) => (
                <Skeleton.Node className="w-full h-64" key={i} />
              ))
            ) : (
              <ProjectViewAlbumAttachments
                maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
                fileList={fileList}
                deletingIds={deletingIds}
                addFilesAllowed={addFilesAllowed}
                currentUserId={userId}
                deleteForeignAllowed={deleteForeignAllowed}
                deleteAllowed={deleteAllowed}
                editAllowed={editAllowed}
                editForeignAllowed={editForeignAllowed}
                allowedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
                onRemoveAttachment={handleRemoveAttachment}
                onRename={handleRename}
                onPreviewFile={handlePreviewFile}
                onBeforeUpload={handleBeforeUpload}
                // onCancelUpload={handleCancelUpload}
              />
            )}
          </div>
        )}
      </Card>

      {isEditModalOpen && (
        <EditAlbumModal
          album={album}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleEditSubmit}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteAlbumModal
          albumName={album?.name}
          fileCount={album?.attachments_count ?? 0}
          isDeleting={isDeletingAlbum}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={(files) => void handleDelete(files)}
        />
      )}
      {isAttachmentSelectorOpen && (
        <AttachmentSelector
          projectId={projectId}
          existingAttachmentIds={attachments.map((a) => a.id)}
          onClose={() => setIsAttachmentSelectorOpen(false)}
          onSubmit={handleAddAttachments}
        />
      )}

      {previewFile && (
        <FileViewer
          file={previewFile}
          items={previewItems}
          onClose={() => setPreviewFile(null)}
          fileActions={viewerFileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          albumContext={{ id: album.id, name: album.name }}
          onRename={(f) => setRenamingFileItem(f)}
        />
      )}

      {renamingFileItem && (
        <RenameFileModal
          fileName={renamingFileItem.name}
          onClose={() => setRenamingFileItem(null)}
          onSubmit={(next) => handleRename(renamingFileItem.id, next)}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  album: state.album.album,
  filters: state.album.albumFilters,
  fetching: state.album.fetchingAlbum,
  projectPermissions: selectProjectWritePermissions(state)
})

const mapDispatchToProps = {
  getAlbumById: getAlbum,
  getFilterOptions: fetchAlbumFilterOptions,
  addAlbumAttachments,
  removeAlbumAttachments,
  updateAlbum,
  setProp: albumSetProp,
  deleteAlbum,
  renameAttachment
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewAlbumSection)
